import { type FC } from 'react'
import { useNavigate } from 'react-router'
import { ImageDemoHomeScreen } from '@egw/assets'
import { useAuth } from '@egw/auth'
import { Button, ButtonVariant, Logo } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { WEALTH_ROUTES } from '@egw/constants'

// Demo page component based on Figma design
export const DemoPage: FC = () => {
  const { t } = useTranslation('features-system')
  const navigate = useNavigate()
  const { pingSession, isAuthenticated } = useAuth()

  const handleGetStarted = async (): Promise<void> => {
    if (!isAuthenticated) {
      await pingSession()
    }
    void navigate(WEALTH_ROUTES.OVERVIEW.path)
  }

  return (
    <div className="bg-background-main h-full overflow-y-auto p-8 sm:p-16">
      <div className="max-w-7xl mx-auto bg-background-surface rounded-3xl shadow-xl p-8 sm:p-16 flex gap-16">
        <div className="flex-1">
          {/* Logo */}
          <div className="w-10 h-10 bg-black flex items-center justify-center mb-12 mx-auto sm:mx-0">
            <Logo className="fill-white w-6 h-6" />
          </div>
          {/* Title */}
          <h1 className="typo-primary-4xl-light text-text-header mb-4">{t('demo.title')}</h1>
          <p className="typo-primary-md-regular text-text-body mb-8">{t('demo.description')}</p>

          <div className="block lg:hidden max-w-sm mb-8">
            <img src={ImageDemoHomeScreen} alt="Demo Home Screen" className="w-full h-full object-contain" />
          </div>

          {/* Features */}
          <div className="space-y-2xs mb-8">
            {/* Your Money Feature */}
            <div className="flex pl-sm py-2xs gap-2xs">
              <div>
                <div className="w-[2px] h-full bg-gray-400" />
              </div>
              <div className="flex-1">
                <p className="typo-primary-base-regular text-text-meta">
                  <span className="typo-primary-base-bold">{t('demo.features.your_money.title')}</span>
                  {` ${t('demo.features.your_money.description')}`}
                </p>
              </div>
            </div>

            {/* Financial Insights Feature */}
            <div className="flex pl-sm py-2xs gap-2xs">
              <div>
                <div className="w-[2px] h-full bg-gray-400" />
              </div>
              <div className="flex-1">
                <p className="typo-primary-base-regular text-text-meta">
                  <span className="typo-primary-base-bold">{t('demo.features.financial_insights.title')}</span>
                  {` ${t('demo.features.financial_insights.description')}`}
                </p>
              </div>
            </div>

            {/* Evergreen Intelligence Feature */}
            <div className="flex pl-sm py-2xs gap-2xs">
              <div>
                <div className="w-[2px] h-full bg-gray-400" />
              </div>
              <div className="flex-1">
                <p className="typo-primary-base-regular text-text-meta">
                  <span className="typo-primary-base-bold">{t('demo.features.evergreen_intelligence.title')}</span>
                  {` ${t('demo.features.evergreen_intelligence.description')}`}
                </p>
              </div>
            </div>
          </div>
          {/* Conclusion */}
          <p className="typo-primary-base-regular text-text-body mb-2">{t('demo.conclusion')}</p>
          <p className="typo-primary-base-regular text-text-body mb-8">{t('demo.date_disclaimer')}</p>
          {/* CTA Button */}
          <Button variant={ButtonVariant.Primary} className="w-fit mb-10 w-full sm:w-min" onClick={handleGetStarted}>
            {t('demo.cta')}
          </Button>
          {/* Disclaimer */}
          <p className="typo-primary-xs-regular text-text-meta mb-2xs">{t('demo.disclaimer_1')}</p>
          <p className="typo-primary-xs-regular text-text-meta">{t('demo.disclaimer_2')}</p>
        </div>

        {/* Right Content - Mockup Image */}
        <div className="hidden lg:block max-w-sm">
          <img src={ImageDemoHomeScreen} alt="Demo Home Screen" className="w-full h-full object-contain" />
        </div>
      </div>
    </div>
  )
}
